/**
 * The flows this domain implements. The entry point of each one declares which it is, and the
 * composition builds a wired copy of the domain per flow, so no use case branches on the value and
 * no call site can forget it.
 */
export const IDENTITY_FLOWS = ['invitation', 'verifyAccount', 'recovery', 'profile'] as const;

/** The rules a password must meet, in the order the checklist shows them. */
export const PASSWORD_RULES = ['minLength', 'uppercase', 'digit'] as const;

/** Minimum password length enforced by the `minLength` rule. */
export const PASSWORD_MIN_LENGTH = 8;

/** The requirements an account can be asked to meet. `DONE` is not one: it is the absence of them. */
export const ACCOUNT_REQUIREMENTS = ['EMAIL', 'PASSWORD', 'PHONE'] as const;

/** The reason a magic link was created. It travels signed inside the token, never in a query param. */
export const LINK_PURPOSES = ['invitation', 'verify_email', 'verify_phone', 'recover'] as const;

/**
 * How strictly the account verification is enforced. The backend decides it, so the gate can be
 * switched off without a release.
 *
 * These are the values this build knows, not the closed set of what may arrive.
 */
export const ENFORCEMENT_MODES = ['off', 'soft', 'hard'] as const;

/** Channels that can deliver a verification link. */
export const VERIFICATION_CHANNELS = ['WHATSAPP', 'SMS', 'EMAIL'] as const;

/**
 * The purposes whose flow continues in this browser after the consume, and which therefore need the
 * link token kept in a cookie.
 *
 * `verify_email` and `verify_phone` are left out: their only screen makes no request, and the cookie
 * is shared by the whole browser, so keeping their token would overwrite the one a wizard is using
 * in another tab.
 */
export const TOKEN_PERSISTING_LINK_PURPOSES = ['invitation', 'recover'] as const;

/**
 * What authorized a request of this domain: the token of a link, the Parrot session, or nothing at
 * all on the one open endpoint.
 *
 * It is what an ambiguous rejection is read against. A missing credential means an unusable link on
 * one side and a dead session on the other, and only the caller knows which it sent.
 */
export const REQUEST_AUTHORIZERS = ['link', 'session', 'none'] as const;
